import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Rocket, Building2, CheckCircle2, Quote, Lightbulb, Zap, Search, BrainCircuit, ArrowDown, ArrowDownUp,
} from 'lucide-react';
import PabloLogo from '../utils/PabloLogo';
import './WinTogether.css';

const audiences = [
  {
    id: 'startups',
    label: 'Startups',
    icon: Rocket,
    title: 'Build the right thing.',
    desc: "Find out whether an idea is worth pursuing, give your coding tool a clear brief and review what it builds. Keep the decisions and lessons available for the next task.",
    moments: [
      {
        stage: 'Pre-build',
        heading: 'Thinking through an idea',
        body: 'Research who would use it, compare the alternatives and choose what to test before spending weeks building.',
      },
      {
        stage: 'In-build',
        heading: 'Actively building',
        body: 'When your AI tool suggests a change, check whether it fits the goal. Save the decision and brief the tool before it edits more code.',
        primary: true,
      },
      {
        stage: 'Post-build',
        heading: 'Questioning what was built',
        body: 'Bring customer feedback and test results to Pabs. Work out what needs fixing and what to try next.',
      },
    ],
    benefits: [
      'Read the same brief and design direction in your connected AI tools',
      'Keep a record of decisions and the work linked to them',
      'Know which questions still need your call',
      'Check an idea before committing time and budget',
    ],
    testimonial: {
      text: "Pabs cut our pre-build discovery time by 60%. It doesn't just agree with you. It challenges your thinking, and that's exactly what we needed. We uploaded our user interview transcripts into Add Learning and watched the strategy improve instantly. Total light-bulb moment.",
      author: 'Sean Olanrewaju',
      role: 'Founder @ medly.nz',
    },
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    icon: Building2,
    // Enterprise positioning follows the Design Authority guide and its
    // enterprise adoption roadmap: governed AI delivery, scoped to each plan.
    title: 'Let more people build with AI, guided by your standards.',
    desc: 'Your product and design leads set the direction, governing knowledge and decision boundaries. Pabs brings that expertise into your teams’ AI tools: briefing agents, reviewing their work and returning the decisions that need human judgment.',
    benefits: [
      'Give builders guidance from your approved company knowledge',
      'Review work against product, brand, accessibility and design-system rules',
      'Delegate routine design decisions within limits your leads set',
      'Keep review evidence, corrections and consequential decisions traceable',
    ],
    testimonial: {
      text: 'Pabs provides the accountability we need for our innovation labs. It codifies the "why" behind every pivot.',
      author: 'Timothy Nesdale',
      role: 'Director @ EPAM',
    },
  },
];

// SVG circuit background
const CircuitBackground = () => (
  <svg
    className="absolute inset-0 w-full h-full opacity-20 pointer-events-none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <pattern id="win-grid" width="40" height="40" patternUnits="userSpaceOnUse">
      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" />
    </pattern>
    <rect width="100%" height="100%" fill="url(#win-grid)" />
    <defs>
      <radialGradient id="win-fade" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="rgba(23, 25, 35, 0)" />
        <stop offset="100%" stopColor="rgba(23, 25, 35, 1)" />
      </radialGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#win-fade)" />
  </svg>
);

const ConnectorLine = ({ className, vertical = false, reverse = false }) => (
  <div
    className={`relative overflow-hidden bg-brand-800/50 ${
      vertical ? 'w-[1px] h-full' : 'h-[1px] w-full'
    } ${className}`}
  >
    <motion.div
      animate={
        vertical
          ? { y: reverse ? ['100%', '-100%'] : ['-100%', '100%'] }
          : { x: reverse ? ['100%', '-100%'] : ['-100%', '100%'] }
      }
      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
      className={`absolute inset-0 ${
        vertical
          ? 'h-1/2 w-full bg-gradient-to-b from-transparent via-brand-200 to-transparent'
          : 'w-1/2 h-full bg-gradient-to-r from-transparent via-brand-200 to-transparent'
      } blur-[1px]`}
    />
  </div>
);

const LearningEngineTag = ({ className, label = 'Customer feedback' }) => (
  <div
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/20 backdrop-blur-md shadow-lg whitespace-nowrap z-30 ${className}`}
  >
    <BrainCircuit className="w-3 h-3 text-white" />
    <span className="text-[9px] font-bold text-white uppercase tracking-wider">{label}</span>
  </div>
);

const PabsCenterNode = ({ className }) => (
  <div
    className={`w-24 h-24 shrink-0 bg-[#171923] rounded-2xl border border-brand-500 shadow-[0_0_40px_rgba(56,189,248,0.2)] flex items-center justify-center relative z-20 group ${className}`}
  >
    <div className="absolute inset-0 rounded-2xl border border-brand-400/20 group-hover:border-brand-400/40 transition-colors"></div>
    <div
      className="w-12 h-12 text-brand-50"
      style={{ '--fill-0': '#f8fafc', '--stroke-0': '#f8fafc' }}
    >
      <PabloLogo />
    </div>
  </div>
);

const NodeCard = ({ icon: Icon, title, sub, color = 'brand', compact = false }) => (
  <div
    className={`relative flex items-center gap-3 p-3 rounded-xl border backdrop-blur-md z-10 shadow-xl shrink-0 ${compact ? 'min-w-0 w-full' : 'min-w-[150px]'}
    ${color === 'brand' ? 'bg-[#1A202C]/90 border-brand-700' : ''}
    ${color === 'emerald' ? 'bg-[#064E3B]/90 border-emerald-800' : ''}
    ${color === 'amber' ? 'bg-[#78350F]/90 border-amber-800' : ''}
    ${color === 'blue' ? 'bg-blue-950/90 border-blue-800' : ''}
    ${color === 'purple' ? 'bg-purple-950/90 border-purple-800' : ''}
  `}
  >
    <Icon
      className={`w-8 h-8 shrink-0 stroke-[1.5] transition-all duration-300
      ${color === 'brand' ? 'text-brand-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]' : ''}
      ${color === 'emerald' ? 'text-emerald-400 drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]' : ''}
      ${color === 'amber' ? 'text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.5)]' : ''}
      ${color === 'blue' ? 'text-blue-400 drop-shadow-[0_0_8px_rgba(96,165,250,0.5)]' : ''}
      ${color === 'purple' ? 'text-purple-400 drop-shadow-[0_0_8px_rgba(192,132,252,0.5)]' : ''}
    `}
    />
    <div className="flex flex-col">
      <span
        className={`text-[9px] font-bold uppercase tracking-wider ${
          color === 'brand'
            ? 'text-brand-400'
            : color === 'emerald'
            ? 'text-emerald-400'
            : color === 'amber'
            ? 'text-amber-400'
            : color === 'blue'
            ? 'text-blue-400'
            : 'text-purple-400'
        }`}
      >
        {sub || 'Node'}
      </span>
      <span className="text-xs font-bold text-brand-50 whitespace-nowrap">{title}</span>
    </div>
  </div>
);

function StartupVisual() {
  const aiTools = [
    { name: 'Cursor', color: 'bg-blue-400' },
    { name: 'Windsurf', color: 'bg-cyan-400' },
    { name: 'Lovable', color: 'bg-pink-400' },
    { name: 'Bolt', color: 'bg-yellow-400' },
    { name: 'Claude', color: 'bg-orange-400' },
  ];

  return (
    <div className="w-full h-full bg-[#0d1017] relative overflow-hidden flex flex-col items-center justify-center py-8">
      <CircuitBackground />

      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="flex gap-4 mb-2 relative z-10"
      >
        <NodeCard icon={Lightbulb} title="Your idea" sub="Starting point" color="brand" />
        <NodeCard icon={Search} title="Market research" sub="What you know" color="brand" />
      </motion.div>

      <div className="h-10 flex flex-col items-center justify-center relative z-0">
        <ConnectorLine vertical className="h-full" />
      </div>

      <div className="grid grid-cols-3 items-center w-full max-w-lg my-2 gap-4 relative z-20">
        <div />
        <div className="flex justify-center">
          <PabsCenterNode />
        </div>
        <div className="flex items-center -ml-4">
          <div className="w-8 h-[1px] bg-white/20" />
          <LearningEngineTag />
        </div>
      </div>

      <div className="h-10 flex flex-col items-center justify-center relative z-0">
        <ConnectorLine vertical className="h-full" />
      </div>

      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex gap-4 relative z-10"
      >
        <NodeCard icon={CheckCircle2} title="Current plan" sub="Saved in Pabs" color="emerald" />
        <NodeCard icon={Zap} title="Build brief" sub="For your AI tool" color="emerald" />
      </motion.div>

      <div className="h-6 flex flex-col items-center justify-center relative z-0">
        <ConnectorLine vertical className="h-full" />
      </div>

      <motion.div
        initial={{ y: 16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex flex-col items-center gap-2 relative z-10"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-[1px] bg-white/10" />
          <span className="text-[8px] text-white/35 uppercase tracking-[0.25em] font-bold">
            Live in AI vibe code tools
          </span>
          <div className="w-10 h-[1px] bg-white/10" />
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {aiTools.map((tool) => (
            <div
              key={tool.name}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.04] border border-white/10 backdrop-blur-sm"
            >
              <div className={`w-1.5 h-1.5 rounded-full ${tool.color} opacity-80`} />
              <span className="text-[9px] text-white/50 font-semibold tracking-wide">
                {tool.name}
              </span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function EnterpriseVisual() {
  return (
    <figure className="enterprise-example" aria-labelledby="enterprise-example-title">
      <figcaption>
        <h5 id="enterprise-example-title">Your expertise, where teams build.</h5>
      </figcaption>
      <div className="enterprise-knowledge">
        <strong>Approved company knowledge</strong>
        <p>Product direction, brand, design system and accessibility guidance</p>
      </div>
      <ArrowDown className="enterprise-flow-arrow" size={22} aria-hidden="true" />
      <div className="enterprise-authority">
        <div className="enterprise-pabs-name">
          <span className="enterprise-pabs-mark" style={{ '--fill-0': '#f7fafc', '--stroke-0': '#f7fafc' }} aria-hidden="true"><PabloLogo /></span>
          <strong>Pabs</strong>
        </div>
        <p>Product &amp; Design Authority</p>
        <span>Brief agents · Review work · Prepare corrections</span>
      </div>
      <div className="enterprise-participants">
        <div>
          <ArrowDownUp size={22} aria-hidden="true" />
          <strong>Teams &amp; AI tools</strong>
          <p>Build with guidance. Return work and evidence for review.</p>
        </div>
        <div>
          <ArrowDownUp size={22} aria-hidden="true" />
          <strong>Accountable lead</strong>
          <p>Set decision rights. Resolve exceptions and consequential calls.</p>
        </div>
      </div>
      <p className="enterprise-example-decision">Each plan keeps the governing sources, review evidence and decisions together.</p>
      <a className="enterprise-docs-link" href="/docs/design-authority">How Design Authority works</a>
    </figure>
  );
}

export default function WinTogether() {
  const [activeTab, setActiveTab] = useState('enterprise');
  const current = audiences.find((a) => a.id === activeTab);

  return (
    <section id="solutions" className="py-20 md:py-32 bg-brand-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-12 md:mb-20">
          <h2 className="text-[13px] font-bold text-brand-900 uppercase tracking-[0.3em] mb-6">
            Win together
          </h2>
          <h3 className="text-4xl md:text-7xl font-medium text-brand-900 mb-6 md:mb-8">
            Built for founders and teams building with AI.
          </h3>
          <p className="text-lg md:text-xl text-brand-600 font-light leading-relaxed max-w-2xl mx-auto">
            Whether you work alone or lead a team, get help with the research, decisions
            and design reviews that take time away from building.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12 md:mb-16">
          <div className="inline-flex w-full max-w-md flex-wrap md:w-auto md:max-w-none md:flex-nowrap bg-white rounded-2xl md:rounded-[2rem] border border-brand-100 shadow-sm overflow-hidden p-1">
            {audiences.map((aud) => (
              <button
                key={aud.id}
                type="button"
                aria-pressed={activeTab === aud.id}
                onClick={() => setActiveTab(aud.id)}
                className={`flex flex-1 md:flex-none items-center justify-center gap-2 px-3 md:px-8 py-3 md:py-4 text-sm font-bold transition-all duration-300 rounded-xl md:rounded-[1.8rem] ${
                  activeTab === aud.id
                    ? 'bg-brand-900 text-brand-50 shadow-md'
                    : 'bg-transparent text-brand-400 hover:text-brand-900 hover:bg-brand-50'
                }`}
              >
                <aud.icon
                  className={`w-4 h-4 ${activeTab === aud.id ? 'text-brand-50' : 'text-current'}`}
                />
                {aud.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto">
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-stretch"
            >
              {/* Left: Text & Testimonial */}
              <div className="flex flex-col justify-center space-y-10 py-4">
                <div>
                  <h4 className="text-4xl md:text-5xl font-medium text-brand-900 mb-6 leading-tight">
                    {current.title}
                  </h4>
                  <p className="text-lg text-brand-600 leading-relaxed mb-8">{current.desc}</p>

                  {current.moments && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
                      {current.moments.map((m, i) => (
                        <div
                          key={i}
                          className={`p-5 rounded-2xl border ${
                            m.primary
                              ? 'bg-brand-900 border-brand-900 text-white'
                              : 'bg-brand-50 border-brand-100 text-brand-900'
                          }`}
                        >
                          <div className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${m.primary ? 'text-brand-400' : 'text-brand-400'}`}>
                            {m.stage}
                          </div>
                          <div className={`text-sm font-bold mb-2 leading-snug ${m.primary ? 'text-white' : 'text-brand-900'}`}>
                            {m.heading}
                          </div>
                          <p className={`text-xs leading-relaxed ${m.primary ? 'text-brand-400' : 'text-brand-500'}`}>
                            {m.body}
                          </p>
                          {m.primary && (
                            <div className="mt-3 text-[10px] font-bold text-brand-400 uppercase tracking-widest">
                              Strongest signal
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <ul className="space-y-4">
                    {current.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-3 text-brand-900 font-semibold">
                        <CheckCircle2 className="w-5 h-5 text-brand-900 shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="p-8 rounded-[2rem] bg-white border border-brand-100 relative group shadow-sm hover:shadow-md transition-all">
                  <Quote className="absolute top-6 right-8 w-10 h-10 text-brand-100 group-hover:text-brand-200 transition-colors" />
                  <p className="text-brand-700 italic mb-6 relative z-10 text-lg leading-relaxed">
                    "{current.testimonial.text}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="font-bold text-brand-900">{current.testimonial.author}</div>
                      <div className="text-xs text-brand-500 font-bold uppercase tracking-wider">
                        {current.testimonial.role}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Visual */}
              {current.id === 'enterprise' ? <EnterpriseVisual /> : <div className="relative min-h-[420px] md:min-h-[600px] md:h-auto">
                <div className="w-full h-full rounded-[3rem] overflow-hidden shadow-2xl shadow-brand-900/20 border border-brand-200 group bg-[#0d1017] flex items-center justify-center">
                  <div className="w-[138%] h-full scale-[0.72] sm:w-[112%] sm:scale-90 md:w-full md:scale-100">
                    {current.id === 'startups' && <StartupVisual />}
                  </div>
                </div>
              </div>}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
